import { useState, useEffect } from 'react';
import { EditorWorkspace } from '../components/EditorWorkspace';
import { useDarkMode } from '../context/DarkModeContext';
import { useNavigate, useParams, useSearchParams, useLocation, Link } from 'react-router';
import { api } from '../lib/api';
import { useSubscriptionStatus } from '../context/SubscriptionContext';
import { AlertCircle, ArrowLeft } from 'lucide-react';

export default function ReportEditor() {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [activeSection, setActiveSection] = useState('situation');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const customerIdParam = searchParams.get('customerId');
  const [initialReport, setInitialReport] = useState<any>(null);
  const [loading, setLoading] = useState(!!id || !!customerIdParam);
  // Bestehender Bericht ließ sich nicht laden (z.B. 429 Anfrage-Limit) → Hinweis statt
  // leerem Editor, der wie ein neuer Bericht aussähe.
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const { status, loading: subLoading } = useSubscriptionStatus();

  useEffect(() => {
    // /editor und /editor/:id teilen sich diese Komponente — ohne Zurücksetzen bliebe beim
    // Wechsel per „Editor"-Menüpunkt der vorher geöffnete Bericht stehen.
    setInitialReport(null);
    setLoadError(false);
    setLoading(!!id || !!customerIdParam);
    if (id) {
      api.get(`/api/reports/${id}`)
        .then((report) => {
          setInitialReport(report);
          setLoading(false);
        })
        .catch(() => {
          setLoadError(true);
          setLoading(false);
        });
    } else if (customerIdParam) {
      // New report opened for a specific customer (e.g. "Neuer Bericht" from Kundenakte):
      // pre-select the customer. No report id -> the new-report metadata form still shows.
      api.get(`/api/customers/${customerIdParam}`)
        .then((customer) => {
          setInitialReport({ customer });
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [id, customerIdParam, id ? null : location.key, retry]);

  const handleGenerateDraft = () => {
    console.log('Generating AI draft');
  };

  const handleRegenerateDraft = () => {
    console.log('Regenerating draft');
  };

  const handleInsertBlock = () => {
    console.log('Inserting text block');
  };

  const handleCompletenessCheck = () => {
    console.log('Running completeness check');
  };

  // Block creating a NEW report when the plan limit is reached.
  // Existing reports (id present in URL) remain editable.
  const isCreatingNew = !id;
  const usage = status?.reportUsage;
  const isAtLimit = !!usage && usage.used >= usage.limit;
  const showLimitGate = isCreatingNew && !subLoading && status && isAtLimit;

  return (
    <div className={`h-[calc(100vh-3.5rem)] flex ${darkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {showLimitGate ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className={`max-w-md w-full rounded-xl border shadow-sm p-6 sm:p-8 ${
              darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            }`}>
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  darkMode ? 'bg-amber-900/40' : 'bg-amber-100'
                }`}>
                  <AlertCircle className={`w-5 h-5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                </div>
                <div className="flex-1">
                  <h2 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    Berichtslimit erreicht
                  </h2>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {usage?.mode === 'monthly'
                      ? `Sie haben Ihr Monatslimit von ${usage.limit} Berichten ausgeschöpft (${usage.used}/${usage.limit}).`
                      : `Ihr Plan erlaubt ${usage?.limit ?? 1} Bericht${(usage?.limit ?? 1) === 1 ? '' : 'e'} — bereits ${usage?.used ?? 0} angelegt.`}
                    {' '}Mit einem Upgrade können Sie weitere Berichte erstellen.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Link
                  to="/preise"
                  className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm cursor-pointer"
                >
                  Plan upgraden
                </Link>
                <button
                  onClick={() => navigate('/')}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors font-medium shadow-sm border cursor-pointer ${
                    darkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Zur Übersicht
                </button>
              </div>
            </div>
          </div>
        ) : loading || (isCreatingNew && subLoading) ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-slate-500 dark:text-slate-400">
              {isCreatingNew ? 'Plan wird geprüft...' : 'Bericht wird geladen...'}
            </div>
          </div>
        ) : loadError ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className={`max-w-md w-full rounded-xl border shadow-sm p-6 text-center ${
              darkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
            }`}>
              <AlertCircle className={`w-8 h-8 mx-auto mb-3 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
              <p className="font-medium mb-1">Bericht konnte nicht geladen werden.</p>
              <p className={`text-sm mb-5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Bitte kurz warten und erneut versuchen.</p>
              <button
                onClick={() => setRetry(n => n + 1)}
                className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer"
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        ) : (
          <EditorWorkspace
            // Neuer Bericht: bei jedem Aufruf frisch aufbauen (location.key), bestehender: je id.
            key={id ?? `neu-${location.key}`}
            sectionId={activeSection}
            onSectionChange={setActiveSection}
            onGenerateDraft={handleGenerateDraft}
            onRegenerateDraft={handleRegenerateDraft}
            onInsertBlock={handleInsertBlock}
            onCompletenessCheck={handleCompletenessCheck}
            darkMode={darkMode}
            onToggleDarkMode={toggleDarkMode}
            onNavigateSettings={() => navigate('/settings')}
            onNavigateBack={() => navigate('/')}
            initialReport={initialReport}
          />
        )}
      </div>
    </div>
  );
}
